import React, { useEffect, useState } from "react";
import api from "../lib/api";

// --- Helpers ---
function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().slice(0, 10);
  } catch {
    return s;
  }
}

function HeroStat({ label, value, hint }) {
  return (
    <div
      className="surface-1 p-6"
      style={{ borderLeft: "2px solid var(--highlight)" }}
      data-testid="amb-hero-stat"
    >
      <p className="label-eyebrow mb-2">{label}</p>
      <p
        className="font-display font-light leading-none"
        style={{ color: "var(--highlight)", fontSize: "3rem" }}
      >
        {value ?? "—"}
      </p>
      {hint && (
        <p className="text-xs text-neutral-500 mt-3 italic">{hint}</p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="surface-1 p-5" data-testid={`amb-stat-${label.replace(/\s+/g, "-")}`}>
      <p className="label-eyebrow mb-1">{label}</p>
      <p className="font-display text-2xl font-light">{value ?? "—"}</p>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="bento-card p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="label-eyebrow">{title}</p>
        {count != null && (
          <p className="text-[10px] uppercase tracking-widest text-neutral-600">
            {count} {count === 1 ? "row" : "rows"}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function CandidateTable({ rows, threshold, testid }) {
  if (!rows?.length) {
    return (
      <p className="text-neutral-500 text-sm italic py-2">
        Nobody in this cohort yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
            <th className="text-left font-normal py-2 px-2">player</th>
            <th className="text-right font-normal py-2 px-2">total</th>
            <th className="text-right font-normal py-2 px-2">reports</th>
            <th className="text-right font-normal py-2 px-2">videos</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.user_id}
              className="border-t"
              style={{ borderColor: "var(--border-default)" }}
            >
              <td className="py-2 px-2 text-neutral-200">{r.name || "—"}</td>
              <td className="text-right py-2 px-2">
                <span className="text-neutral-100">{r.total}</span>
                <span className="text-neutral-600">/{threshold}</span>
              </td>
              <td className="text-right py-2 px-2 text-neutral-400">
                {r.match_reports}
              </td>
              <td className="text-right py-2 px-2 text-neutral-400">
                {r.video_reviews}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AmbassadorDashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: payload } = await api.get(
          "/admin/ambassador-dashboard?since_days=30"
        );
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail || "Couldn't load ambassador dashboard"
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  return (
    <div className="mb-12" data-testid="ambassador-dashboard-section">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <p className="label-eyebrow">ambassador pipeline</p>
        <button
          data-testid="amb-refresh"
          onClick={() => setRefreshTick((t) => t + 1)}
          className="px-3 py-1 text-xs rounded text-neutral-500 hover:text-neutral-300"
        >
          ↻
        </button>
      </div>

      {loading && (
        <p
          className="text-neutral-600 text-xs py-4"
          data-testid="amb-loading"
        >
          Loading…
        </p>
      )}

      {error && (
        <p
          className="text-neutral-500 text-sm italic py-4"
          data-testid="amb-error"
        >
          {error}
        </p>
      )}

      {!loading && !error && data && (
        <div className="space-y-6" data-testid="amb-content">
          {/* HERO: the leading indicator */}
          <div className="grid md:grid-cols-3 gap-4">
            <HeroStat
              label={`at ${(data.threshold ?? 3) - 1}/${data.threshold ?? 3} — about to graduate`}
              value={data.candidates_at_threshold_minus_1}
              hint="Leading indicator. When this number climbs, new ambassadors land automatically."
            />
            <Stat
              label="total ambassadors"
              value={data.total_ambassadors}
            />
            <Stat
              label="new this week"
              value={data.new_ambassadors_this_week_count}
            />
          </div>

          {/* Cohort tables */}
          <Section
            title={`${(data.threshold ?? 3) - 1}/${data.threshold ?? 3} cohort — closest to badge`}
            testid="amb-cohort-one-away"
            count={data.candidates_one_away?.length}
          >
            <p className="text-xs text-neutral-500 mb-3 italic">
              These users are one report or video review away. Worth a personal
              nudge.
            </p>
            <CandidateTable
              rows={data.candidates_one_away || []}
              threshold={data.threshold ?? 3}
              testid="amb-cohort-one-table"
            />
          </Section>

          <Section
            title={`${(data.threshold ?? 3) - 2}/${data.threshold ?? 3} cohort — building`}
            testid="amb-cohort-two-away"
            count={data.candidates_two_away?.length}
          >
            <CandidateTable
              rows={data.candidates_two_away || []}
              threshold={data.threshold ?? 3}
              testid="amb-cohort-two-table"
            />
          </Section>

          <Section
            title="top referrers"
            testid="amb-top-referrers"
            count={data.top_referrers?.length}
          >
            {(data.top_referrers || []).length === 0 ? (
              <p className="text-neutral-500 text-sm italic py-2">
                No referrals attributed yet.
              </p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table
                  className="w-full text-sm"
                  data-testid="amb-top-referrers-table"
                >
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
                      <th className="text-left font-normal py-2 px-2">code</th>
                      <th className="text-left font-normal py-2 px-2">player</th>
                      <th className="text-right font-normal py-2 px-2">joins</th>
                      <th className="text-right font-normal py-2 px-2">status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_referrers.map((r) => (
                      <tr
                        key={r.code}
                        className="border-t"
                        style={{ borderColor: "var(--border-default)" }}
                      >
                        <td className="py-2 px-2 text-neutral-200 font-mono text-xs">
                          {r.code}
                        </td>
                        <td className="py-2 px-2 text-neutral-300">
                          {r.name || r.email || "—"}
                        </td>
                        <td className="text-right py-2 px-2 text-neutral-200">
                          {r.verified_joins}
                        </td>
                        <td className="text-right py-2 px-2 text-[11px] text-neutral-500">
                          {r.ambassador_status || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            title="reports completed per user"
            testid="amb-reports-per-user"
            count={data.reports_completed_per_user?.length}
          >
            {(data.reports_completed_per_user || []).length === 0 ? (
              <p className="text-neutral-500 text-sm italic py-2">
                No reports recorded yet.
              </p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table
                  className="w-full text-sm"
                  data-testid="amb-reports-per-user-table"
                >
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
                      <th className="text-left font-normal py-2 px-2">player</th>
                      <th className="text-right font-normal py-2 px-2">total</th>
                      <th className="text-right font-normal py-2 px-2">reports</th>
                      <th className="text-right font-normal py-2 px-2">videos</th>
                      <th className="text-right font-normal py-2 px-2">badge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reports_completed_per_user.slice(0, 50).map((r) => (
                      <tr
                        key={r.user_id}
                        className="border-t"
                        style={{ borderColor: "var(--border-default)" }}
                      >
                        <td className="py-2 px-2 text-neutral-200">
                          {r.name || "—"}
                        </td>
                        <td className="text-right py-2 px-2 text-neutral-100">
                          {r.total}
                        </td>
                        <td className="text-right py-2 px-2 text-neutral-400">
                          {r.match_reports}
                        </td>
                        <td className="text-right py-2 px-2 text-neutral-400">
                          {r.video_reviews}
                        </td>
                        <td className="text-right py-2 px-2">
                          {r.is_ambassador ? (
                            <span style={{ color: "var(--highlight)" }}>🎖</span>
                          ) : (
                            <span className="text-neutral-700">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <p
            className="text-[10px] text-neutral-700 italic"
            data-testid="amb-generated-at"
          >
            generated · {fmtDate(data.generated_at)}
          </p>
        </div>
      )}
    </div>
  );
}
